import jsPDF from 'jspdf';
import 'jspdf-autotable';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

// Helper function to format dates using native Date API
export const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'Invalid Date';
  
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

// Helper function to convert 24-hour time to 12-hour format with AM/PM
export const formatTimeTo12Hour = (timeString) => {
  if (!timeString || timeString === 'N/A') return 'N/A';
  
  // Handle time formats like "09:30:00" or "09:30"
  const timeParts = timeString.split(':');
  if (timeParts.length < 2) return timeString;
  
  let hours = parseInt(timeParts[0], 10);
  const minutes = timeParts[1];
  
  if (isNaN(hours)) return timeString;
  
  const period = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12; // Convert 0 to 12 for midnight
  
  return `${hours}:${minutes} ${period}`;
};

export const exportSummaryToPDF = (summaryData, month) => {
  const doc = new jsPDF('landscape', 'mm', 'a4');
  
  doc.setFontSize(16);
  doc.text('Attendance Summary', 14, 15);
  doc.setFontSize(12);
  doc.text(`Month: ${month}`, 14, 22);
  
  // Extract month name for headers
  const monthName = month.split(' ')[0]; // e.g., "March" from "March 2026"
  const ytdRange = `Jan-${monthName.substring(0, 3)}`; // e.g., "Jan-Mar"
  
  const tableData = summaryData.map(row => [
    row.slNo,
    row.name,
    row.designation,
    formatDate(row.joiningDate),
    row.leaveTakenYTD || 0,
    row.leaveDatesCurrent || '-',
    row.totalLeaveCurrent,
    row.totalLateDaysYTD || 0,
    row.lateDatesBreakdown || '-',
    row.totalLateDays,
    row.totalAbsentDaysYTD || 0,
    row.absentDatesCurrent || '-',
    row.absentDaysCurrent || 0,
    row.missingTaskDaysYTD || 0,
    row.missingTaskDatesCurrent || '-',
    row.missingTaskDaysCurrent || 0
  ]);
  
  doc.autoTable({
    head: [[
      'Sl', 'Name', 'Designation', 'Join Date',
      `Leave (${ytdRange})`, `Leave Dates (${monthName})`, `Total Leave (${monthName})`,
      `Late (${ytdRange})`, `Late Dates (${monthName})`, `Total Late (${monthName})`,
      `Absent (${ytdRange})`, `Absent Dates (${monthName})`, `Total Absent (${monthName})`,
      `Missing Task (${ytdRange})`, `Missing Task Dates (${monthName})`, `Total Missing Task (${monthName})`
    ]],
    body: tableData,
    startY: 30,
    styles: { 
      fontSize: 6,
      lineColor: [0, 0, 0],
      lineWidth: 0.1
    },
    headStyles: {
      fillColor: [240, 240, 240],
      textColor: [0, 0, 0],
      lineColor: [0, 0, 0],
      lineWidth: 0.2
    },
    bodyStyles: {
      lineColor: [0, 0, 0],
      lineWidth: 0.1
    },
    alternateRowStyles: {
      fillColor: [250, 250, 250]
    }
  });
  
  // Get the final Y position after table
  const finalY = doc.lastAutoTable.finalY;
  
  // Add timestamp and prepared by section
  const now = new Date();
  const timestamp = now.toLocaleString('en-US', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }).replace(',', '');
  
  doc.setFontSize(10);
  doc.text(`Generated on: ${timestamp}`, 14, finalY + 10);
  doc.text('Prepared by: _______________________', 14, finalY + 20);
  
  // Use month name for filename (e.g., "March 2026" -> "March_2026")
  const filename = `Attendance_Summary_${month.replace(' ', '_')}.pdf`;
  doc.save(filename);
};

export const exportSummaryToExcel = async (summaryData, month) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Attendance Summary');
  
  // Extract month name for headers
  const monthName = month.split(' ')[0]; // e.g., "March" from "March 2026"
  const ytdRange = `January to ${monthName}`; // e.g., "January to March"
  
  worksheet.columns = [
    { header: 'Sl No', key: 'slNo', width: 8 },
    { header: 'Name', key: 'name', width: 20 },
    { header: 'Designation', key: 'designation', width: 15 },
    { header: 'Joining Date', key: 'joiningDate', width: 15 },
    { header: `Leave (${ytdRange})`, key: 'leaveTakenYTD', width: 18 },
    { header: `Leave Dates (${monthName})`, key: 'leaveDatesCurrent', width: 20 },
    { header: `Total Leave (${monthName})`, key: 'totalLeaveCurrent', width: 15 },
    { header: `Late (${ytdRange})`, key: 'totalLateDaysYTD', width: 18 },
    { header: `Late Dates (${monthName})`, key: 'lateDatesBreakdown', width: 20 },
    { header: `Total Late (${monthName})`, key: 'totalLateDays', width: 15 },
    { header: `Absent (${ytdRange})`, key: 'totalAbsentDaysYTD', width: 18 },
    { header: `Absent Dates (${monthName})`, key: 'absentDatesCurrent', width: 20 },
    { header: `Total Absent (${monthName})`, key: 'absentDaysCurrent', width: 15 },
    { header: `Missing Task (${ytdRange})`, key: 'missingTaskDaysYTD', width: 22 },
    { header: `Missing Task Dates (${monthName})`, key: 'missingTaskDatesCurrent', width: 25 },
    { header: `Total Missing Task (${monthName})`, key: 'missingTaskDaysCurrent', width: 18 }
  ];
  
  summaryData.forEach(row => {
    worksheet.addRow({
      ...row,
      joiningDate: formatDate(row.joiningDate),
      leaveTakenYTD: row.leaveTakenYTD || 0,
      leaveDatesCurrent: row.leaveDatesCurrent || '-',
      totalLateDaysYTD: row.totalLateDaysYTD || 0,
      lateDatesBreakdown: row.lateDatesBreakdown || '-',
      totalAbsentDaysYTD: row.totalAbsentDaysYTD || 0,
      absentDatesCurrent: row.absentDatesCurrent || '-',
      absentDaysCurrent: row.absentDaysCurrent || 0,
      missingTaskDaysYTD: row.missingTaskDaysYTD || 0,
      missingTaskDatesCurrent: row.missingTaskDatesCurrent || '-',
      missingTaskDaysCurrent: row.missingTaskDaysCurrent || 0
    });
  });
  
  const buffer = await workbook.xlsx.writeBuffer();
  const [mm, yyyy] = month.split('/');
  saveAs(new Blob([buffer]), `Attendance Summary ${mm}${yyyy}.xlsx`);
};

export const exportDetailsToPDF = (detailsData, month) => {
  const doc = new jsPDF('landscape', 'mm', 'a4');
  
  doc.setFontSize(16);
  doc.text(`${detailsData.name} - Attendance Details`, 14, 15);
  doc.setFontSize(10);
  doc.text(`Designation: ${detailsData.designation}`, 14, 22);
  doc.text(`Joining Date: ${formatDate(detailsData.joiningDate)}`, 14, 28);
  doc.text(`Month: ${month}`, 14, 34);
  
  const tableData = detailsData.dailyBreakdown.map(row => [
    formatDate(row.date),
    formatTimeTo12Hour(row.loginTime) || 'N/A',
    formatTimeTo12Hour(row.logoutTime) || 'N/A',
    row.locationName || 'N/A',
    row.locationNameLogout || 'N/A',
    row.late ? 'Yes' : 'No',
    row.status,
    row.missingTask ? 'Yes' : 'No'
  ]);
  
  doc.autoTable({
    head: [['Date', 'Login Time', 'Logout Time', 'Check-in Location', 'Check-out Location', 'Late', 'Status', 'Missing Task']],
    body: tableData,
    startY: 40,
    styles: { 
      fontSize: 7,
      lineColor: [0, 0, 0],
      lineWidth: 0.1
    },
    headStyles: {
      fillColor: [240, 240, 240],
      textColor: [0, 0, 0],
      lineColor: [0, 0, 0],
      lineWidth: 0.2
    },
    bodyStyles: {
      lineColor: [0, 0, 0],
      lineWidth: 0.1
    },
    alternateRowStyles: {
      fillColor: [250, 250, 250]
    }
  });
  
  // Get the final Y position after table
  const finalY = doc.lastAutoTable.finalY;
  
  // Add timestamp and prepared by section
  const now = new Date();
  const timestamp = now.toLocaleString('en-US', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }).replace(',', '');
  
  doc.setFontSize(10);
  doc.text(`Generated on: ${timestamp}`, 14, finalY + 10);
  doc.text('Prepared by: _______________________', 14, finalY + 20);
  
  const [mm, yyyy] = month.split('/');
  doc.save(`${detailsData.name} Attendance Details ${mm}${yyyy}.pdf`);
};

export const exportDetailsToExcel = async (detailsData, month) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Attendance Details');
  
  // Add employee info header
  worksheet.addRow(['Employee Name:', detailsData.name]);
  worksheet.addRow(['Designation:', detailsData.designation]);
  worksheet.addRow(['Joining Date:', formatDate(detailsData.joiningDate)]);
  worksheet.addRow(['Month:', month]);
  worksheet.addRow([]);
  
  // Add table
  worksheet.columns = [
    { header: 'Date', key: 'date', width: 15 },
    { header: 'Login Time', key: 'loginTime', width: 12 },
    { header: 'Logout Time', key: 'logoutTime', width: 12 },
    { header: 'Check-in Location', key: 'locationName', width: 25 },
    { header: 'Check-out Location', key: 'locationNameLogout', width: 25 },
    { header: 'Late', key: 'late', width: 8 },
    { header: 'Status', key: 'status', width: 20 },
    { header: 'Missing Task', key: 'missingTask', width: 12 }
  ];
  
  detailsData.dailyBreakdown.forEach(row => {
    worksheet.addRow({
      date: formatDate(row.date),
      loginTime: formatTimeTo12Hour(row.loginTime) || 'N/A',
      logoutTime: formatTimeTo12Hour(row.logoutTime) || 'N/A',
      locationName: row.locationName || 'N/A',
      locationNameLogout: row.locationNameLogout || 'N/A',
      late: row.late ? 'Yes' : 'No',
      status: row.status,
      missingTask: row.missingTask ? 'Yes' : 'No'
    });
  });
  
  const buffer = await workbook.xlsx.writeBuffer();
  const [mm, yyyy] = month.split('/');
  saveAs(new Blob([buffer]), `${detailsData.name} Attendance Details ${mm}${yyyy}.xlsx`);
};

// New export functions for tasks
export const exportDetailsTasksToPDF = (detailsData, month) => {
  const doc = new jsPDF('landscape', 'mm', 'a4');
  
  doc.setFontSize(16);
  doc.text(`${detailsData.name} - Daily Tasks Report`, 14, 15);
  doc.setFontSize(10);
  doc.text(`Designation: ${detailsData.designation}`, 14, 22);
  doc.text(`Joining Date: ${formatDate(detailsData.joiningDate)}`, 14, 28);
  doc.text(`Month: ${month}`, 14, 34);
  
  const tableData = detailsData.dailyBreakdown.map(row => {
    // Check if it's a special day (Friday, Holiday, Leave, Absent)
    const status = row.status;
    let tasksContent = '';
    
    if (status === 'Friday' || status.includes('Holiday') || status.includes('Leave') || status === 'Absent') {
      tasksContent = status;
    } else {
      // Combine all tasks into a single string
      const tasks = [];
      if (row.dailyTask1) tasks.push(`1. ${row.dailyTask1}`);
      if (row.dailyTask2) tasks.push(`2. ${row.dailyTask2}`);
      if (row.dailyTask3) tasks.push(`3. ${row.dailyTask3}`);
      if (row.dailyTask4) tasks.push(`4. ${row.dailyTask4}`);
      if (row.lateTask) tasks.push(`Late Reason: ${row.lateTask}`);
      
      tasksContent = tasks.length > 0 ? tasks.join('\n') : 'No tasks recorded';
    }
    
    return [
      formatDate(row.date),
      tasksContent
    ];
  });
  
  doc.autoTable({
    head: [['Date', 'Daily Tasks']],
    body: tableData,
    startY: 40,
    styles: { 
      fontSize: 8,
      lineColor: [0, 0, 0],
      lineWidth: 0.1,
      cellPadding: 3
    },
    headStyles: {
      fillColor: [240, 240, 240],
      textColor: [0, 0, 0],
      lineColor: [0, 0, 0],
      lineWidth: 0.2,
      fontStyle: 'bold'
    },
    bodyStyles: {
      lineColor: [0, 0, 0],
      lineWidth: 0.1
    },
    alternateRowStyles: {
      fillColor: [250, 250, 250]
    },
    columnStyles: {
      0: { cellWidth: 30 },
      1: { cellWidth: 'auto' }
    }
  });
  
  // Get the final Y position after table
  const finalY = doc.lastAutoTable.finalY;
  
  // Add timestamp and prepared by section
  const now = new Date();
  const timestamp = now.toLocaleString('en-US', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }).replace(',', '');
  
  doc.setFontSize(10);
  doc.text(`Generated on: ${timestamp}`, 14, finalY + 10);
  doc.text('Prepared by: _______________________', 14, finalY + 20);
  
  const filename = `${detailsData.name}_Daily_Tasks_${month.replace(' ', '_')}.pdf`;
  doc.save(filename);
};

export const exportDetailsTasksToExcel = async (detailsData, month) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Daily Tasks');
  
  // Add employee info header
  worksheet.addRow(['Employee Name:', detailsData.name]);
  worksheet.addRow(['Designation:', detailsData.designation]);
  worksheet.addRow(['Joining Date:', formatDate(detailsData.joiningDate)]);
  worksheet.addRow(['Month:', month]);
  worksheet.addRow([]);
  
  // Define columns with proper widths
  worksheet.columns = [
    { header: 'Date', key: 'date', width: 15 },
    { header: 'Daily Task 1', key: 'task1', width: 40 },
    { header: 'Daily Task 2', key: 'task2', width: 40 },
    { header: 'Daily Task 3', key: 'task3', width: 40 },
    { header: 'Daily Task 4', key: 'task4', width: 40 },
    { header: 'Late Reason', key: 'lateTask', width: 40 }
  ];
  
  // Add data rows
  detailsData.dailyBreakdown.forEach(row => {
    const status = row.status;
    let rowData;
    
    // Check if it's a special day (Friday, Holiday, Leave, Absent)
    if (status === 'Friday' || status.includes('Holiday') || status.includes('Leave') || status === 'Absent') {
      rowData = {
        date: formatDate(row.date),
        task1: status,
        task2: '-',
        task3: '-',
        task4: '-',
        lateTask: '-'
      };
    } else {
      rowData = {
        date: formatDate(row.date),
        task1: row.dailyTask1 || '-',
        task2: row.dailyTask2 || '-',
        task3: row.dailyTask3 || '-',
        task4: row.dailyTask4 || '-',
        lateTask: row.lateTask || '-'
      };
    }
    
    const newRow = worksheet.addRow(rowData);
    
    // Enable text wrapping for all cells in this row
    newRow.eachCell((cell) => {
      cell.alignment = { 
        vertical: 'top', 
        horizontal: 'left', 
        wrapText: true 
      };
    });
  });
  
  // Style the header row (row 6, after the employee info)
  const headerRow = worksheet.getRow(6);
  headerRow.font = { bold: true };
  headerRow.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFE0E0E0' }
  };
  headerRow.alignment = { vertical: 'middle', horizontal: 'center' };
  
  // Add borders to all cells with data
  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber >= 6) { // Only add borders to table rows
      row.eachCell((cell) => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
      });
    }
  });
  
  const buffer = await workbook.xlsx.writeBuffer();
  const filename = `${detailsData.name}_Daily_Tasks_${month.replace(' ', '_')}.xlsx`;
  saveAs(new Blob([buffer]), filename);
};
